import React from 'react';
import {
  TableRow,
  TableRowColumn,
  Avatar,
  FontIcon,
  IconButton,
  IconMenu,
  MenuItem,
} from 'material-ui';
import RedDot from '../../tc-ui/redDot';
import { getShownState, getPriorityColor } from '../contractingUtils';
import moment from 'moment';
import R from 'ramda';
import pluralize from 'pluralize';
import ArchivedIcon from 'material-ui/svg-icons/content/archive';
import { SmallFlatButton } from '../../tc-ui/small-buttons';
import UpdatePriority from './update-rate-conversion-priority';
import UpdateAssignee from '../generic-components/update-assignee';
import ArrowDropDownIcon from 'material-ui/svg-icons/navigation/arrow-drop-down';

const RateConversionRowDumb =
  ({
     contract,
     actions,
     showFolder,
     showContractYear,
     inSearchResult,
     openContacts,
   }) => {
    const noReadAccess = !R.contains('read', contract.access);
    const {
      viewingBy,
      locationId,
      respondedContracts,
      viewerIsSender,
      partners,
      to = {},
    } = contract;
    const tcContractsInRequest = Boolean(!locationId && respondedContracts && respondedContracts.length);
    const shownState = getShownState({ companyType: viewingBy, ...contract });
    const hasVersion = !R.either(R.isNil, R.isEmpty)(contract.versions);
    const goIntoContractOrWizard = noReadAccess ? () => {} : () => {
      if (contract.viewingBy === 'Supplier' && !contract.locationId && !contract.buyerChecked) actions.goToSupplierWizard(contract._id);
      else actions.viewContract('contract', contract._id);
    };
    const hasEditingDraft = contract.hasDraft && contract.locationId && contract.viewingBy === 'Supplier';
    const readOnly = !R.contains('write', contract.access);
    const unread = !viewerIsSender && !contract.isViewed;
    const requestText = (() => {
      if (tcContractsInRequest && viewingBy === 'Contractor') {
        return <span style={{ color: 'green' }}>{respondedContracts.length} {pluralize('contracts', respondedContracts.length)}</span>;
      }
      if (!locationId && viewingBy === 'Supplier' && shownState !== 'sent') {
        return <span>Prepared for {to.name}</span>;
      }
      return '';
    })();
    const {
      displayName,
      logoToDisplay,
    } = (() => {
      if (viewerIsSender) {
        return {
          displayName: contract.to.name,
          displayAvatar: contract.to.avatar,
        };
      }
      // if viewer is receiver
      // display based receiver's partner list
      const partner = partners[0] || {};
      return {
        displayName: partner.name || contract.from.name,
        logoToDisplay: partner.avatar || contract.from.avatar,
      };
    })();
    const displayLoc = Boolean(locationId && displayName !== contract.locationName);
    return (
      <TableRow
        selectable={false}
        data-cy={`${contract._id}contractRow`}
        style={{ height: '56px', overflow: 'visible' }}
      >
        {
          inSearchResult && (<TableRowColumn colSpan="1"/>)
        }
        <TableRowColumn
          colSpan="1"
          style={{ padding: inSearchResult ? '0' : '0 16px', overflow: 'visible' }}
        >
          {
            R.call(R.cond([
              [() => logoToDisplay,
                R.always(<Avatar src={logoToDisplay} style={{ marginTop: '4px' }}/>)],
              [R.T, R.always(<Avatar>{R.head(displayName || '')}</Avatar>)],
            ]))
          }
        </TableRowColumn>
        <TableRowColumn
          colSpan="6"
          style={{ padding: 0, color: noReadAccess ? 'rgba(0, 0, 0, 0.44)' : '' }}
        >
          <span
            style={{ height: '48px', display: 'table-cell', verticalAlign: 'middle', cursor: noReadAccess ? '' : 'pointer' }}
            onClick={goIntoContractOrWizard}
          >
            <div style={{ fontWeight: unread ? 'bold' : '', lineHeight: '12px', fontSize: '12px' }}>
              <div>
                {viewerIsSender ? contract.to.name : displayName}&nbsp;
                <small><i>{hasVersion ? `(v.${R.path(['versions', 'length'], contract) + 1})` : ''}</i></small>
                <small
                  style={{ color: 'rgba(0, 0, 0, 0.44)' }}
                  data-cy={`folder-${contract.folders.join(', ')}`}
                >{showFolder && !R.isEmpty(contract.folders) ? ` (${contract.folders.join(', ')})` : ''}</small>
                <small style={{ color: 'red' }}>{showFolder && contract.isDeleted ? 'Trash' : ''}</small>
                <small style={{ color: 'red' }}>{showFolder && contract.isArchived ? 'Archived' : ''}</small>
              </div>
            </div>
            <div style={{ fontWeight: unread ? 'bold' : '', display: 'flex', alignItems: 'center', lineHeight: '16px', fontSize: '12px' }}>
              {
                displayLoc && (
                  <FontIcon className="tc-icon-location_on" style={{ fontSize: '16px', color: 'rgba(0, 0, 0, 0.54)', marginRight: '8px' }} />
                )
              }
              {
                displayLoc && contract.locationName
              }
              {
                R.path(['supplierInfo', 'marketInfo'], contract) ? (<small>
                  &nbsp;{`(${R.path(['supplierInfo', 'marketInfo'], contract)})`}
                </small>) : <span/>
              }
              {
                hasEditingDraft ? (<span
                  style={{ marginLeft: '12px', color: 'red' }}
                >
                This contract has a&nbsp;
                  <a href={`/contracting/draft/${contract._id}/details`} onClick={e => e.stopPropagation()}>
                draft
                </a>.
              </span>) : <span/>
              }
              {requestText}
              <span style={{ color: 'rgba(0, 0, 0, 0.44)' }}> {readOnly && '(READ-ONLY)'}</span>
            </div>
          </span>
        </TableRowColumn>
        <TableRowColumn
          colSpan="2"
          style={{ padding: '0 0 0 8px', fontSize: '10px' }}
        >
          {contract.rateConversionStatus}
        </TableRowColumn>
        <TableRowColumn
          colSpan="1"
          style={{ padding: '0 0 0 8px', fontSize: '10px' }}
        >
          <UpdatePriority
            priorty={contract.priority || 'Medium'}
            contractId={contract._id}
            button={
              <SmallFlatButton
                labelStyle={{ padding: 0, fontSize: '10px', color: getPriorityColor(contract.priority || 'Medium') }}
                icon={<ArrowDropDownIcon style={{ fill: 'rgba(0, 0, 0, 0.54)', margin: 0 }}/>}
                labelPosition="before"
                label={contract.priority || 'Medium'}
              />
            }
          />
        </TableRowColumn>
        <TableRowColumn
          colSpan="1"
          style={{ padding: 0, fontSize: '10px' }}
        >
          <UpdateAssignee
            assignee={contract.assignee}
            contractId={contract._id}
            isContainer
            files={contract.files}
          />
        </TableRowColumn>
        {
          showContractYear && (
            <TableRowColumn colSpan="1" style={{ padding: 0, textAlign: 'center', whiteSpace: 'pre-wrap', fontSize: '10px' }}>
              {`${moment.utc(contract.startDate).format('D MMM YY')} -\n${moment.utc(contract.endDate).format('D MMM YY')}  `}
            </TableRowColumn>
          )
        }
        <TableRowColumn
          colSpan="1"
          style={{ padding: 0, fontSize: '11px', textAlign: 'center' }}
        >
          {moment(contract.updatedAt).calendar(null, {
            sameDay: 'h:mm a',
            lastDay: 'D MMM YY',
            lastWeek: 'D MMM YY',
            sameElse: 'D MMM YY',
          })}
        </TableRowColumn>
        <TableRowColumn
          colSpan="2"
          style={{ textOverflow: 'none', paddingLeft: 0, paddingRight: 0 }}
        >
          <div style={{ display: 'flex' }}>
            <IconButton
              onClick={openContacts}
              style={{ padding: '12px 4px' }}
              iconStyle={{ color: 'rgba(0, 0, 0, 0.54)' }}
            >
              <FontIcon className="tc-icon-mail_outline" style={{ fontSize: '20px', top: '4px' }} />
            </IconButton>
            <IconButton
              onClick={() => actions.viewNotes('contract', contract._id)}
              style={{ padding: '12px 4px' }}
              iconStyle={{ color: 'rgba(0, 0, 0, 0.54)' }}
            >
              <FontIcon className="tc-icon-note_add" style={{ color: 'rgba(0, 0, 0, 0.54)' }} />
              { contract.notesCount
                && <RedDot style={{ top: '-10px', left: '5px' }} color="#00bcd4" />}
            </IconButton>
            <IconButton
              onClick={() => actions.viewActivities('contract', contract._id)}
              style={{ padding: '12px 4px' }}
              iconStyle={{ color: 'rgba(0, 0, 0, 0.54)' }}
            >
              <FontIcon className="tc-icon-question_answer" style={{ color: 'rgba(0, 0, 0, 0.54)' }} />
              { contract.hasNewComments && <RedDot style={{ top: '-10px', left: '6px' }} />}
            </IconButton>
            <IconMenu
              iconButtonElement={<IconButton iconClassName="tc-icon-more-vert" style={{ padding: '12px 4px', width: '28px' }} iconStyle={{ color: '#616061' }} />}
              anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
              targetOrigin={{ horizontal: 'right', vertical: 'top' }}
              desktop
            >
              {
                contract.isArchived ? (
                  <MenuItem leftIcon={<ArchivedIcon />} primaryText="Unarchive" onTouchTap={() => actions.restoreContractFromArchived(contract._id)}/>
                ) : (
                  <MenuItem leftIcon={<ArchivedIcon />} primaryText="Archive" onTouchTap={() => actions.moveToArchived(contract._id)}/>
                )
              }
            </IconMenu>
          </div>
        </TableRowColumn>
      </TableRow>
    );
  };

export default RateConversionRowDumb;



// WEBPACK FOOTER //
// ./src/app/components/contracting/dummy-account/rate-conversion-row-dumb.jsx